import GroupsIcon from "@mui/icons-material/Groups";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SearchIcon from "@mui/icons-material/Search";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { AuthContext } from "@bw/shared/hooks";

import { CustomerPicker } from "@/components/customer-picker";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import { listDailyBookings, type Booking } from "@/libs/bookingsApi";
import { listCourts, type Court } from "@/libs/facilityApi";
import {
  createZonedTimeFormatter,
  toZonedCivilDate,
} from "@/libs/zonedDateTime";

import { BookingRosterDrawer } from "./booking-roster-drawer";
import {
  QUICK_DROPIN_FACILITY_REQUIREMENT,
  QuickDropinDialog,
  reconcileQuickDropinAttemptStorage,
} from "./quick-dropin-dialog";
import { RedeemPassDialog } from "./redeem-pass-dialog";

interface CheckInSnapshot {
  facilityId: string;
  bookings: Booking[];
  courts: Court[];
  bookingError: string | null;
  courtError: string | null;
}

const STATUS_COLORS = {
  confirmed: "primary",
  completed: "success",
  cancelled: "error",
} as const;

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function humanizeStatus(status: string): string {
  if (!status) return "—";
  const words = status.replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function bookingIdentity(booking: Booking): string {
  const displayName = booking.display_name?.trim();
  const userId = booking.user_id?.trim();
  return displayName && displayName !== userId ? displayName : "Reserved";
}

function isRosterBooking(booking: Booking): boolean {
  return Boolean(booking.user_id?.trim()) && !booking.event_name?.trim();
}

export function CheckInWorkspace() {
  const auth = useContext(AuthContext);
  const hasAuthContext = auth !== null;
  const authLoading = auth?.loading ?? false;
  const operatorUid = auth?.user?.uid ?? null;
  const {
    facility,
    facilityId,
    isLoading: facilityLoading,
  } = useResolvedFacility();

  useEffect(() => {
    if (!hasAuthContext || authLoading) return;
    reconcileQuickDropinAttemptStorage(operatorUid);
  }, [authLoading, hasAuthContext, operatorUid]);

  if (
    (hasAuthContext && (authLoading || !operatorUid)) ||
    facilityLoading ||
    !facilityId
  ) {
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{
          minHeight: "40vh",
          display: "grid",
          placeItems: "center",
          px: { xs: 2, lg: 3 },
          color: "text.secondary",
        }}
      >
        <Typography variant="body2">Loading…</Typography>
      </Box>
    );
  }

  return (
    <CheckInFacilityWorkspace
      key={facilityId}
      facility={facility}
      facilityId={facilityId}
      operatorUid={operatorUid}
    />
  );
}

function CheckInFacilityWorkspace({
  facility,
  facilityId,
  operatorUid,
}: {
  facility: ReturnType<typeof useResolvedFacility>["facility"];
  facilityId: string;
  operatorUid: string | null;
}) {
  const facilityTimeZone = facility?.timezone || "America/Los_Angeles";
  const facilityTimeFormatter = useMemo(
    () => createZonedTimeFormatter(facilityTimeZone),
    [facilityTimeZone],
  );
  const [snapshot, setSnapshot] = useState<CheckInSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [rosterBooking, setRosterBooking] = useState<Booking | null>(null);
  const rosterButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const rosterFocusTarget = useRef<string | null>(null);
  const quickDropinAvailable =
    facility?.status === "active" && facility.approval_status === "approved";

  useEffect(() => {
    let cancelled = false;
    const targetFacilityId = facilityId;
    const today = toZonedCivilDate(new Date(), facilityTimeZone);
    setLoading(true);

    void (async () => {
      const [bookingResult, courtResult] = await Promise.allSettled([
        listDailyBookings(targetFacilityId, today),
        listCourts(targetFacilityId),
      ]);
      if (cancelled) return;

      setSnapshot({
        facilityId: targetFacilityId,
        bookings:
          bookingResult.status === "fulfilled" ? bookingResult.value : [],
        courts: courtResult.status === "fulfilled" ? courtResult.value : [],
        bookingError:
          bookingResult.status === "rejected"
            ? errorMessage(
                bookingResult.reason,
                "Failed to load booking rosters.",
              )
            : null,
        courtError:
          courtResult.status === "rejected"
            ? errorMessage(courtResult.reason, "Court names are unavailable.")
            : null,
      });
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [facilityId, facilityTimeZone]);

  const activeSnapshot = snapshot?.facilityId === facilityId ? snapshot : null;
  const currentLoading = loading || activeSnapshot === null;
  const bookings = activeSnapshot?.bookings ?? [];
  const courtNames = useMemo(
    () =>
      new Map(
        (activeSnapshot?.courts ?? []).map((court) => [
          court.court_id,
          court.name,
        ]),
      ),
    [activeSnapshot?.courts],
  );

  const visibleBookings = useMemo(() => {
    const rosterBookings = bookings
      .filter(isRosterBooking)
      .slice()
      .sort((left, right) => left.start_time.localeCompare(right.start_time));
    const query = search.trim().toLocaleLowerCase();
    if (!query) return rosterBookings;
    return rosterBookings.filter((booking) =>
      bookingIdentity(booking).toLocaleLowerCase().includes(query),
    );
  }, [bookings, search]);

  return (
    <Stack spacing={3} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
      <Box>
        <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
          Check-in
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {facility?.name ?? "Selected facility"}
        </Typography>
      </Box>

      <Paper
        component="section"
        variant="outlined"
        aria-labelledby="check-in-actions-title"
        sx={{ p: 2 }}
      >
        <Stack spacing={2}>
          <Typography id="check-in-actions-title" component="h2" variant="h6">
            Check in a player
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <RedeemPassDialog facilityId={facilityId} />
            {quickDropinAvailable ? (
              <QuickDropinDialog
                key={`${facilityId}:${operatorUid}`}
                facilityId={facilityId}
                facilityName={facility?.name ?? "Selected facility"}
                operatorUid={operatorUid}
                CustomerPickerComponent={CustomerPicker}
              />
            ) : (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ alignItems: { sm: "center" } }}
              >
                <Button
                  disabled
                  variant="outlined"
                  startIcon={<PersonAddAlt1Icon />}
                  sx={{ "&.Mui-disabled": { pointerEvents: "auto" } }}
                >
                  Quick drop-in
                </Button>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  role="status"
                >
                  {QUICK_DROPIN_FACILITY_REQUIREMENT}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Paper>

      {rosterBooking ? null : (
        <Paper
          component="section"
          variant="outlined"
          role="region"
          aria-labelledby="booking-rosters-title"
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{
              alignItems: { sm: "center" },
              justifyContent: "space-between",
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography id="booking-rosters-title" component="h2" variant="h6">
              Booking rosters
            </Typography>
            <TextField
              type="search"
              size="small"
              placeholder="Search by name"
              value={search}
              sx={{ width: { xs: "100%", sm: 320 } }}
              slotProps={{
                htmlInput: { "aria-label": "Search booking rosters" },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon aria-hidden="true" />
                    </InputAdornment>
                  ),
                },
              }}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Stack>

          {activeSnapshot?.bookingError ? (
            <Alert severity="error" sx={{ m: 2, mb: 0 }}>
              {activeSnapshot.bookingError}
            </Alert>
          ) : null}
          {activeSnapshot?.courtError ? (
            <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
              {activeSnapshot.courtError}
            </Alert>
          ) : null}

          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" aria-label="Booking rosters">
              <TableHead>
                <TableRow>
                  <TableCell>Player</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell>Court</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Roster</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentLoading ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Skeleton variant="rounded" height={44} />
                    </TableCell>
                  </TableRow>
                ) : activeSnapshot?.bookingError ? null : visibleBookings.length ===
                  0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        role="status"
                        sx={{ py: 3, textAlign: "center" }}
                      >
                        {search.trim()
                          ? "No booking rosters match."
                          : "No reservations with player rosters scheduled for today."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleBookings.map((booking) => (
                    <TableRow key={booking.booking_id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {bookingIdentity(booking)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {facilityTimeFormatter.format(
                          new Date(booking.start_time),
                        )}{" "}
                        –{" "}
                        {facilityTimeFormatter.format(
                          new Date(booking.end_time),
                        )}
                      </TableCell>
                      <TableCell>
                        {activeSnapshot.courtError
                          ? "—"
                          : (courtNames.get(booking.court_id) ?? "—")}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={humanizeStatus(booking.status)}
                          size="small"
                          color={
                            STATUS_COLORS[
                              booking.status as keyof typeof STATUS_COLORS
                            ] ?? "default"
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          ref={(button) => {
                            if (button) {
                              rosterButtonRefs.current.set(
                                booking.booking_id,
                                button,
                              );
                            } else {
                              rosterButtonRefs.current.delete(
                                booking.booking_id,
                              );
                            }
                          }}
                          size="small"
                          variant="outlined"
                          startIcon={<GroupsIcon />}
                          onClick={() => {
                            rosterFocusTarget.current = booking.booking_id;
                            setRosterBooking(booking);
                          }}
                        >
                          Roster
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <BookingRosterDrawer
        facilityId={facilityId}
        booking={rosterBooking}
        onOpenChange={(open) => {
          if (!open) setRosterBooking(null);
        }}
        onClosed={() => {
          const bookingId = rosterFocusTarget.current;
          rosterFocusTarget.current = null;
          if (bookingId) rosterButtonRefs.current.get(bookingId)?.focus();
        }}
      />
    </Stack>
  );
}
